import { useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BarChart3, RefreshCw, TrendingUp, Store, Package, Search, AlertTriangle } from "lucide-react";
import { useExecutiveSummaryDashboard } from "@/hooks/useExecutiveSummaryDashboard";

type WindowWeeks = 4 | 8 | 12;

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(Math.round(value));
}

function formatRatio(value: number): string {
  return value.toFixed(2);
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export default function ExecutiveSummaryDashboard() {
  const [windowWeeks, setWindowWeeks] = useState<WindowWeeks>(8);
  const { weeklyData, merchantData, kpis, isLoading, isError, error, refetch } = useExecutiveSummaryDashboard(windowWeeks);

  const trendData = useMemo(() => {
    return weeklyData.map((row) => ({
      ...row,
      weekLabel: format(parseISO(row.week_start), "MMM d"),
    }));
  }, [weeklyData]);

  const hasData = weeklyData.length > 0 || merchantData.length > 0;

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-primary" />
              Executive Summary
            </h1>
            <p className="text-muted-foreground">
              Immutable publish snapshot analytics for deals and merchant scan outcomes.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Select value={String(windowWeeks)} onValueChange={(v) => setWindowWeeks(Number(v) as WindowWeeks)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Window" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="4">Last 4 weeks</SelectItem>
                <SelectItem value="8">Last 8 weeks</SelectItem>
                <SelectItem value="12">Last 12 weeks</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => void refetch()} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Deals Published</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold">{formatNumber(kpis.dealsPublished)}</div>
              <p className="text-xs text-muted-foreground mt-1">Primary production output</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Deals Found</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold">{formatNumber(kpis.dealsFound)}</div>
              <p className="text-xs text-muted-foreground mt-1">Detected before final publish filtering</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Merchants Scanned</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold">{formatNumber(kpis.merchantsScanned)}</div>
              <p className="text-xs text-muted-foreground mt-1">Weekly scan coverage total</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Merchants with Deals Found</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold">{formatNumber(kpis.merchantsWithDealsFound)}</div>
              <p className="text-xs text-muted-foreground mt-1">How many produced at least one deal</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Deals Found / Scan Unit</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold">{formatRatio(kpis.dealsFoundPerScanUnit)}</div>
              <p className="text-xs text-muted-foreground mt-1">Efficiency baseline for scan value</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Zero-Deal Merchant Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold">{formatPercent(kpis.zeroDealMerchantRate)}</div>
              <p className="text-xs text-muted-foreground mt-1">Share of merchant-weeks with no deals found</p>
            </CardContent>
          </Card>
        </div>

        {isError ? (
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Failed to load Executive Summary
              </CardTitle>
              <CardDescription>
                {error instanceof Error ? error.message : "Unknown dashboard query error"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={() => void refetch()}>Retry</Button>
            </CardContent>
          </Card>
        ) : null}

        {!isError && !isLoading && !hasData ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No publish snapshots found in the selected window.
            </CardContent>
          </Card>
        ) : null}

        {!isError && hasData ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Weekly Deals Trend
                </CardTitle>
                <CardDescription>Deals found vs deals published by week</CardDescription>
              </CardHeader>
              <CardContent className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="weekLabel" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="total_deals_found" name="Deals Found" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="total_deals_published" name="Deals Published" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Store className="h-4 w-4" />
                  Weekly Merchant Scan Volume
                </CardTitle>
                <CardDescription>Merchants scanned each week</CardDescription>
              </CardHeader>
              <CardContent className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="weekLabel" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="merchants_scanned" name="Merchants Scanned" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        ) : null}

        {!isError && hasData ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4" />
                Merchant Yield ({windowWeeks}w)
              </CardTitle>
              <CardDescription>
                Sorted by highest scan units, then lowest deals-per-scan ratio.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Merchant</TableHead>
                    <TableHead className="text-right">Scans Completed</TableHead>
                    <TableHead className="text-right">Scan Units</TableHead>
                    <TableHead className="text-right">Deals Found</TableHead>
                    <TableHead className="text-right">Deals Published</TableHead>
                    <TableHead className="text-right">Deal/Scan Ratio</TableHead>
                    <TableHead className="text-right">Zero-Deal Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {merchantData.slice(0, 100).map((row) => (
                    <TableRow key={row.merchant_source_id}>
                      <TableCell className="font-medium">{row.merchant_name}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.scans_completed)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.total_scan_units)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.total_deals_found)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.total_deals_published)}</TableCell>
                      <TableCell className="text-right">{formatRatio(row.avg_deal_per_scan_ratio)}</TableCell>
                      <TableCell className="text-right">{formatPercent(row.zero_deal_rate)}</TableCell>
                    </TableRow>
                  ))}
                  {merchantData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        <div className="inline-flex items-center gap-2">
                          <Search className="h-4 w-4" />
                          No merchant rows for this window
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </AdminLayout>
  );
}
